import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { Box, Typography, Chip, Stack, Divider, CircularProgress, Button, Grid, Card, CardContent, CardHeader, Dialog, DialogContent, DialogActions, Alert, MenuItem, Menu, IconButton, Tooltip } from '@mui/material'
import { CheckCircleOutlineRounded, PendingOutlined, MoneyOffOutlined, CancelOutlined, InfoOutlined, WarningOutlined, ArrowBackOutlined } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { PageHeader } from 'components/PageHeader'
import { formatPhoneNumber } from 'utils/helpers'
import { paymentDetail, paymentUpdateStatus, paymentRefund } from '../../models/payment'
import { EVENT_PAYMENT_UPDATE_SUCCESS } from '../../models/events/payment.event'
import DirhamIcon from '../../assets/images/dirham-symbol.png'

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
const PaymentDetail = () => {
    return (
        <>
            <PageHeader
                title="Payment Detail"
                breadcrumbs={[
                    { path: '/payment', label: 'Payments' },
                    { path: '#',        label: 'Detail' },
                ]}
            />
            <PaymentDetailView />
        </>
    )
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
const PaymentDetailView = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const eventbus = useEventBusContext()

    const [payment, setPayment]   = useState(null)
    const [loading, setLoading]   = useState(true)
    const [error, setError]       = useState(null)

    const fetchPayment = () => {
        setLoading(true)
        paymentDetail(id)
            .then((res) => {
                setPayment(res.data ?? res)
                setError(null)
            })
            .catch(() => setError('Failed to load payment details.'))
            .finally(() => setLoading(false))
    }

    useEffect(() => { fetchPayment() }, [id])

    useEffect(() => {
        return eventbus.on(EVENT_PAYMENT_UPDATE_SUCCESS, (updated) => {
            if (String(updated?.id) === String(id)) {
                setPayment((prev) => ({ ...prev, ...updated }))
            }
        })
    }, [eventbus, id])

    const handleStatusUpdate = (newStatus) => {
        paymentUpdateStatus(id, newStatus)
            .then((res) => {
                const updated = res.data ?? res
                setPayment((prev) => ({ ...prev, status: updated.status }))
                eventbus.fire(EVENT_PAYMENT_UPDATE_SUCCESS, updated)
                enqueueSnackbar('Status updated successfully', { variant: 'success', autoHideDuration: 2000 })
            })
            .catch(() => enqueueSnackbar('Failed to update status', { variant: 'error', autoHideDuration: 2500 }))
    }

    const handleRefund = (reason) => {
        paymentRefund(id, { reason })
            .then((res) => {
                const updated = res.data ?? res
                setPayment((prev) => ({ ...prev, ...updated }))
                enqueueSnackbar('Payment refunded successfully', { variant: 'success', autoHideDuration: 2000 })
            })
            .catch(() => enqueueSnackbar('Failed to refund payment', { variant: 'error', autoHideDuration: 2500 }))
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress size={60} />
            </Box>
        )
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button sx={{ mt: 2 }} onClick={() => navigate('/payment')}>
                    Back to Payments
                </Button>
            </Box>
        )
    }

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
            <Grid container spacing={3}>

                {/* Header row */}
                <Grid item size={{ xs: 12 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Tooltip title="Back to Payments">
                                <IconButton
                                    onClick={() => navigate('/payment')}
                                    color="primary"
                                    size="large"
                                    sx={{
                                        bgcolor: 'primary.lighter',
                                        '&:hover': { bgcolor: 'primary.light' }
                                    }}
                                >
                                    <ArrowBackOutlined fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="h3">
                                {payment.tran_ref ?? `Payment #${payment.id}`}
                            </Typography>
                            <StatusChip status={payment.status} />
                        </Box>

                        <StatusActions
                            payment={payment}
                            onStatusUpdate={handleStatusUpdate}
                            onRefund={handleRefund}
                        />
                    </Box>
                </Grid>

                {/* Payment Information */}
                <Grid item size={{ xs: 12 }}>
                    <Card
                        elevation={0}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                        }}
                    >
                        <CardHeader
                            title={<Typography variant="h4" sx={{ fontWeight: 600 }}>Payment Information</Typography>}
                            sx={{ pb: 2 }}
                        />
                        <CardContent>
                            <Grid container spacing={3}>
                                <Field label="Transaction Ref" mono>{payment.tran_ref ?? 'N/A'}</Field>
                                <Field label="Transaction ID" mono>{payment.transaction_id ?? 'N/A'}</Field>
                                <Field label="Payment Method">
                                    <MethodBadge method={payment.payment_method} />
                                </Field>


                                <Field label="Status">
                                    <StatusChip status={payment.status} />
                                </Field>

                                <Grid item size={{ xs: 12 }}>
                                    <Divider />
                                </Grid>

                                <Field label="Amount" size={{ md: 3, sm: 4, xs: 6 }}>
                                    <CurrencyDisplay amount={payment.amount} />
                                </Field>
                                <Field label="Cart Amount" size={{ md: 3, sm: 4, xs: 6 }}>
                                    {payment.cart_amount ? (
                                        <CurrencyDisplay amount={payment.cart_amount} currency={payment.cart_currency ?? payment.currency} />
                                    ) : 'N/A'}
                                </Field>
                                <Field label="Currency" size={{ md: 3, sm: 4, xs: 6 }}>
                                    {payment.currency || 'N/A'}
                                </Field>
                                <Field label="Cart ID" size={{ md: 3, sm: 4, xs: 6 }}>
                                    {payment.cart_id ?? 'N/A'}
                                </Field>

                                <Grid item size={{ xs: 12 }}>
                                    <Divider />
                                </Grid>

                                <Field label="Response Code">{payment.response_code ?? 'N/A'}</Field>
                                <Field label="Response Message">{payment.response_message ?? 'N/A'}</Field>
                                <Field label="Created At">{formatDate(payment.created_at)}</Field>
                                <Field label="Updated At">{formatDate(payment.updated_at)}</Field>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order */}
                {payment.order && (
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Card
                            elevation={0}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}
                        >
                            <CardHeader
                                title={<Typography variant="h4" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Order</Typography>}
                                sx={{ pb: 2 }}
                            />
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Field label="Order #" size={{ xs: 6 }}>{payment.order.order_number ?? 'N/A'}</Field>
                                    <Field label="Total" size={{ xs: 6 }}>
                                        <CurrencyDisplay amount={payment.order.total} />
                                    </Field>
                                    <Field label="Status" size={{ xs: 6 }}>
                                        <StatusChip status={payment.order.status} />
                                    </Field>
                                </Grid>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => navigate(`/orders/${payment.order_id}`)}
                                    >
                                        View Order
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Customer */}
                {payment.customer && (
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Card
                            elevation={0}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}
                        >
                            <CardHeader
                                title={<Typography variant="h4" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Customer</Typography>}
                                sx={{ pb: 2 }}
                            />
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Field label="Name" size={{ xs: 6 }}>{payment.customer.name ?? 'N/A'}</Field>
                                    <Field label="Email" size={{ xs: 6 }}>{payment.customer.email ?? 'N/A'}</Field>
                                    <Field label="WhatsApp" size={{ xs: 6 }}>{formatPhoneNumber(payment.customer.whatsapp_number) ?? 'N/A'}</Field>
                                </Grid>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => navigate(`/customers/${payment.customer.id}`)}
                                    >
                                        View Customer
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Billing details from PayTabs */}
                {payment.customer_details && (
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <DataCard title="Billing Details" data={payment.customer_details} />
                    </Grid>
                )}

                {/* Shipping details from PayTabs */}
                {payment.shipping_details && (
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <DataCard title="Shipping Details" data={payment.shipping_details} />
                    </Grid>
                )}

                {/* Payment Result */}
                {payment.payment_result && (
                    <Grid item size={{ xs: 12 }}>
                        <DataCard title="Payment Result" data={payment.payment_result} />
                    </Grid>
                )}

                {/* Callback Data */}
                {payment.callback_data && (
                    <Grid item size={{ xs: 12 }}>
                        <DataCard title="Callback Data" data={payment.callback_data} />
                    </Grid>
                )}

            </Grid>
        </Box>
    )
}

// ---------------------------------------------------------------------------
// Status actions toolbar
// ---------------------------------------------------------------------------
const STATUS_OPTIONS = [
    { key: 'pending',   label: 'Pending' },
    { key: 'paid',      label: 'Paid' },
    { key: 'refunded',  label: 'Refunded' },
    { key: 'cancelled', label: 'Cancelled' },
]

const FINAL_STATUSES = ['refunded', 'cancelled']

const StatusActions = ({ payment, onStatusUpdate, onRefund }) => {
    const [anchorEl, setAnchorEl]           = useState(null)
    const [refundDialog, setRefundDialog]   = useState(false)
    const [cancelDialog, setCancelDialog]   = useState(false)

    const isFinal = FINAL_STATUSES.includes(payment.status)

    const handleSelect = (key) => {
        setAnchorEl(null)
        if (key === 'refunded') { setRefundDialog(true);  return }
        if (key === 'cancelled') { setCancelDialog(true); return }
        onStatusUpdate(key)
    }

    return (
        <>
            <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    disabled={isFinal}
                    endIcon={<InfoOutlined />}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    size="large"
                >
                    Update Status
                </Button>

                {payment.status === 'paid' && (
                    <Button
                        variant="contained"
                        color="info"
                        onClick={() => setRefundDialog(true)}
                        size="large"
                    >
                        Refund
                    </Button>
                )}
            </Stack>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {STATUS_OPTIONS.map((opt) => (
                    <MenuItem
                        key={opt.key}
                        selected={opt.key === payment.status}
                        onClick={() => handleSelect(opt.key)}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* Refund dialog */}
            <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
                <DialogContent dividers sx={{ px: 4, py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <MoneyOffOutlined style={{ fontSize: 48, color: '#096dd9' }} />
                        <Typography variant="h5" textAlign="center">
                            Mark this payment as refunded?
                        </Typography>
                        <Alert severity="info" sx={{ width: '100%' }}>
                            Amount: AED {parseFloat(payment.amount ?? 0).toFixed(2)}. This cannot be undone.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button variant="outlined" color="secondary" onClick={() => setRefundDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        onClick={() => { setRefundDialog(false); onRefund('Refunded by admin') }}
                    >
                        Yes, Mark as Refunded
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel dialog */}
            <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
                <DialogContent dividers sx={{ px: 4, py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <WarningOutlined style={{ fontSize: 48, color: '#cf1322' }} />
                        <Typography variant="h5" textAlign="center">
                            Cancel this payment?
                        </Typography>
                        <Alert severity="warning" sx={{ width: '100%' }}>
                            This action cannot be undone.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button variant="outlined" color="secondary" onClick={() => setCancelDialog(false)}>
                        No, Keep Payment
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => { setCancelDialog(false); onStatusUpdate('cancelled') }}
                    >
                        Yes, Cancel Payment
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

// ---------------------------------------------------------------------------
// Reusable field / display sub-components (mirrors OrderDetail's style)
// ---------------------------------------------------------------------------

// Caption-over-value block, same visual pattern used throughout OrderDetail
const Field = ({ label, children, size = { md: 3, sm: 6, xs: 12 }, mono = false }) => (
    <Grid item size={size}>
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'block',
                    mb: 0.5
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    fontFamily: mono ? 'monospace' : 'inherit',
                    wordBreak: 'break-all'
                }}
            >
                {children}
            </Typography>
        </Box>
    </Grid>
)

// A bordered Card that renders an arbitrary data object as a grid of Fields,
// following the same Card/CardHeader/CardContent shell as the rest of the page.
// Nested objects (e.g. customer_details inside callback_data) get their own
// labeled sub-section instead of being dumped as a raw JSON string.
const DataCard = ({ title, data }) => {
    const parsed = typeof data === 'string'
        ? (() => { try { return JSON.parse(data) } catch { return data } })()
        : data

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
                <CardHeader
                    title={<Typography variant="h4" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>{title}</Typography>}
                    sx={{ pb: 2 }}
                />
                <CardContent>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {formatValue(parsed)}
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    const scalarEntries = []
    const nestedEntries = []
    Object.entries(parsed).forEach(([key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            nestedEntries.push([key, value])
        } else {
            scalarEntries.push([key, value])
        }
    })

    return (
        <Card
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}
        >
            <CardHeader
                title={<Typography variant="h4" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>{title}</Typography>}
                sx={{ pb: 2 }}
            />
            <CardContent>
                {scalarEntries.length > 0 && (
                    <Grid container spacing={3}>
                        {scalarEntries.map(([key, value]) => (
                            <Field key={key} label={humanizeKey(key)} size={{ md: 4, sm: 6, xs: 12 }}>
                                {formatValue(value)}
                            </Field>
                        ))}
                    </Grid>
                )}

                {nestedEntries.map(([key, value], idx) => (
                    <Box key={key} sx={{ mt: scalarEntries.length > 0 || idx > 0 ? 3 : 0 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 700, display: 'block', mb: 1.5 }}
                        >
                            {humanizeKey(key)}
                        </Typography>
                        <Grid container spacing={3}>
                            {Object.entries(value).map(([k, v]) => (
                                <Field key={k} label={humanizeKey(k)} size={{ md: 4, sm: 6, xs: 12 }}>
                                    {formatValue(v)}
                                </Field>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </CardContent>
        </Card>
    )
}

const StatusChip = ({ status }) => {
    const STATUS_STYLES = {
        pending:   { bg: '#fff1b8', fg: '#d48806', icon: <PendingOutlined fontSize="small" /> },
        paid:      { bg: '#f6ffed', fg: '#389e0d', icon: <CheckCircleOutlineRounded fontSize="small" /> },
        refunded:  { bg: '#e6f7ff', fg: '#096dd9', icon: <MoneyOffOutlined fontSize="small" /> },
        cancelled: { bg: '#fff2f0', fg: '#cf1322', icon: <CancelOutlined fontSize="small" /> },
    }
    const s = STATUS_STYLES[status] ?? { bg: '#f5f5f5', fg: '#595959', icon: <InfoOutlined fontSize="small" /> }
    const label = STATUS_OPTIONS.find(o => o.key === status)?.label ?? status ?? 'Unknown'
    return (
        <Chip
            label={label}
            size="medium"
            sx={{ backgroundColor: s.bg, color: s.fg, fontWeight: 600, fontSize: '14px' }}
        />
    )
}

const MethodBadge = ({ method }) => (
    <Chip
        label={method === 'cash' ? 'Cash' : 'PayTabs'}
        size="medium"
        sx={{
            backgroundColor: method === 'cash' ? '#f6ffed' : '#e6f7ff',
            color:           method === 'cash' ? '#389e0d' : '#096dd9',
            fontWeight: 600,
            fontSize: '13px',
        }}
    />
)

const CurrencyDisplay = ({ amount, currency = 'AED' }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <img src={DirhamIcon} width={18} alt={currency} />
        <span>{parseFloat(amount ?? 0).toFixed(0)}</span>
    </Stack>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (ts) => {
    if (!ts) return 'N/A'
    return new Date(ts).toLocaleString('en-AE', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    })
}

const humanizeKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A'
    if (Array.isArray(value)) {
        if (value.length === 0) return 'N/A'
        return value.every((v) => v === null || typeof v !== 'object')
            ? value.join(', ')
            : JSON.stringify(value)
    }
    if (typeof value === 'object') return JSON.stringify(value)
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return String(value)
}

export default PaymentDetail